import { useId, type ReactNode } from 'react';
import { Slider } from '@voll-intelligence/ui-kit/ui/slider';
import { FilterSection } from './results_filters_panel.js';

/** Uma faixa de horário em minutos do dia (0 … 1439). */
export type TimeRange = {
  readonly from: number;
  readonly to: number;
};

/** O último minuto do dia — a ponta direita da régua. */
const LAST_MINUTE = 23 * 60 + 59;

/** Passo da régua de horário: meia hora — de 1 em 1 minuto a alça vira agulha. */
const TIME_STEP = 30;

export type TimeRangeRow = {
  /** O nome da régua ("Saída", "Chegada"). */
  readonly label: string;
  /** `undefined` = o dia inteiro (eixo sem corte). */
  readonly value: TimeRange | undefined;
  readonly onChange: (range: TimeRange | undefined) => void;
};

/** Uma régua: o nome à esquerda, a faixa em prosa à direita, o slider embaixo.
 *  Alças nas duas pontas do dia devolvem `undefined` — eixo neutro. */
function TimeRangeSlider({
  row,
  rangeLabel,
  locked,
}: {
  readonly row: TimeRangeRow;
  readonly rangeLabel: (range: TimeRange) => string;
  readonly locked: boolean;
}): ReactNode {
  const labelId = useId();
  const range = row.value ?? { from: 0, to: LAST_MINUTE };
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className="text-ui">
          {row.label}
        </span>
        <span className="text-ui text-muted-foreground tabular-nums">{rangeLabel(range)}</span>
      </div>
      <div className="px-1.5 pt-1 pb-2">
        <Slider
          min={0}
          max={LAST_MINUTE}
          step={TIME_STEP}
          value={[range.from, range.to]}
          aria-labelledby={labelId}
          disabled={locked}
          onValueChange={(next: readonly number[]) => {
            const from = Math.min(...next);
            const to = Math.min(Math.max(...next), LAST_MINUTE);
            row.onChange(from <= 0 && to >= LAST_MINUTE ? undefined : { from, to });
          }}
        />
      </div>
    </div>
  );
}

/**
 * O eixo de horário (o ônibus; a mesma ideia do `Horários` do aéreo): uma seção
 * com as réguas dadas — `Saída` e `Chegada` — cada uma com a faixa em prosa
 * ("00:00 - 23:59"), que o host formata.
 */
export function TimeRangeSection({
  title,
  rows,
  rangeLabel,
  locked = false,
}: {
  readonly title: string;
  readonly rows: readonly TimeRangeRow[];
  readonly rangeLabel: (range: TimeRange) => string;
  readonly locked?: boolean;
}): ReactNode {
  return (
    <FilterSection title={title}>
      <div className="grid gap-4">
        {rows.map((row) => (
          <TimeRangeSlider key={row.label} row={row} rangeLabel={rangeLabel} locked={locked} />
        ))}
      </div>
    </FilterSection>
  );
}
